import { LazySection } from "@/components/common/lazy-section";
import { CustomerCareWidget } from "@/components/customer-care-widget";
import { ErrorBoundary } from "@/components/error-boundary";
import { NearbySUPsClient } from "@/components/nearby-sups/client";
import { LazyNearbyPlacesMap } from "@/components/project-or-sup/lazy-nearby-places-map";
import { SEOContentSetter } from "@/components/seo-content-setter";
import { GalleryLarge } from "@/components/sup/gallery/large";
import { GallerySmall } from "@/components/sup/gallery/small";
import { filterHiddenMedia, getVideoFromMediaList, getVideoCaptionsUrl } from "@/components/sup/gallery/shared";
import { SEOContentForProjectOrSup } from "@/components/sup/seo/content-project-sup";
import { UnitPageTracking } from "@/components/sup/unit-page-tracking";
import { bengaluruGps } from "@/info/bengaluru-gps";
import type { NearbyProperty, SellerUnitsAndProjectsOne } from "@/types/sup";
import { toSentenceCase } from "@/utils/string/to-sentence-case";
import { useMemo } from "react";
import { CopyButton } from "../common/copy-button";
import { Configuration, ProjectConfigurations } from "../configuration";
import { AdditionalFeatures } from "./additional-features";
import { BreadcrumbsWrapper } from "./breadcrumbs-wrapper";
import { SUPErrorFallback } from "./error-fallback";
import { AmenitiesSection } from "./sections/amenities";
import { Flooring } from "./sections/flooring";
import { Furnishing } from "./sections/furnishing";
import { Interiors } from "./sections/interiors";
import { OverviewSection } from "./sections/overview";
import { ProjectDetails } from "./sections/project-details";
import { PropertyDetails } from "./seller-unit/property-details";
import { SectionContent } from "./seller-unit/section-components";
// import { SubUnitsSection } from "./seller-unit/sub-units-section";
import { NearbySupsSkeleton } from "../projects/skeletons";
import { PromotionalCardsSection } from "../promotional-cards/hooks";
import { SubUnitsSection } from "./subunits-section";
import { SUPTitle } from "./title";
const defConfigs: Configuration[] = [];

// SUP Unit Component with improved mobile-first design
export function SellerUnitOne({
  supOne,
  configurations,
  nearByProjects,
  nearByUnits: _nearByUnits,
  supId: id = supOne?._id || "",
  className,
  isLoading = false,
  distanceFromText,
  isInModal = false,
  isVisible = true,
  showDetailsEvenIfUnavailable = false,
  isInPreferenceCard = false
}: {
  configurations?: Configuration[];
  nearByUnits?: NearbyProperty[];
  nearByProjects?: NearbyProperty[];
  supId?: string;
  supOne?: SellerUnitsAndProjectsOne;
  className?: string;
  children?: React.ReactNode;
  isLoading?: boolean;
  distanceFromText?: string;
  isInModal?: boolean;
  isVisible?: boolean;
  isInPreferenceCard?: boolean;
  type?: "sp" | "unit";
  showDetailsEvenIfUnavailable?: boolean;
}) {
  // Memoize coordinates to prevent unnecessary re-renders (must be before early return)
  const coordinates = useMemo(() => {
    if (!supOne?.location?.gps) return null;
    return {
      lat: supOne.location.gps.coordinates[1] || bengaluruGps.latitude,
      lng: supOne.location.gps.coordinates[0] || bengaluruGps.longitude
    };
  }, [supOne?.location?.gps]);
  const mapCoordinates = useMemo(() => {
    if (!supOne?.location?.gps) return null;
    return [supOne.location.gps.coordinates[0] || bengaluruGps.longitude, supOne.location.gps.coordinates[1] || bengaluruGps.latitude] as [number, number];
  }, [supOne?.location?.gps]);
  const galleryVideo = useMemo(() => {
    return getVideoFromMediaList(supOne?.mediaList);
  }, [supOne?.mediaList]);
  const categories = useMemo(() => {
    const visibleMedia = filterHiddenMedia(supOne?.mediaList);
    const imageCategories = visibleMedia?.filter(Boolean)?.filter(media => media.type !== "video") // Exclude video items from categories
    ?.map(({
      space,
      name
    }) => ({
      name: toSentenceCase(space || name),
      code: space
    })).reduce((acc: {
      name: string;
      code: string;
    }[], curr: {
      name: string;
      code: string;
    }) => {
      if (!acc.some(cat => cat.code === curr.code)) {
        acc.push(curr);
      }
      return acc;
    }, []);
    // Add "Videos" category if a video exists
    if (galleryVideo) {
      imageCategories.unshift({
        name: "Videos",
        code: "videos"
      });
    }
    return imageCategories;
  }, [supOne?.mediaList, galleryVideo]);
  const galleryImages = useMemo(() => {
    const visibleMedia_0 = filterHiddenMedia(supOne?.mediaList);
    return visibleMedia_0.filter(media_0 => media_0 && media_0.name !== "Floorplan" && media_0.type !== "video") || [];
  }, [supOne?.mediaList]);
  const floorplans = useMemo(() => {
    return supOne?.mediaList?.filter(media_1 => media_1 && media_1.name === "Floorplan") || [];
  }, [supOne?.mediaList]);
  if (!supOne) {
    return <div className={`${className}`}>
				<div className="flex items-center justify-center h-64">
					<div className="text-gray-500">No SUP data available</div>
				</div>
			</div>;
  }
  const fallbackDistanceFromText = supOne.projectName ? supOne.projectName.split("(")[0].replace(/apartments?/i, "").trim() : supOne.propertyTypes?.[0] ? `this ${supOne.propertyTypes?.[0]}` : "";
  const finalDistanceFromText = distanceFromText || fallbackDistanceFromText;

  // Extract project name for breadcrumbs
  const slug = supOne.slug;
  const cityName = supOne.location?.city;

  // Calculate price strings for Overview title
  // When in preference card or saleStatus is undefined, assume it's available for sale
  const isNotAvailableForSale = isInPreferenceCard ? false : supOne.saleStatus !== "onSale";
  const hasInteriors = Boolean(supOne.spaceSummary?.interiors?.length);
  const hasFlooring = Boolean(supOne.spaceSummary?.flooring?.length);
  const hasFurnishing = Boolean(supOne.spaceSummary?.furnishing?.items?.length);
  const sectionsWithContent = [hasInteriors, hasFlooring, hasFurnishing].filter(Boolean).length;

  // Declarative UI visibility flags
  const shouldShowBreadcrumbs = !isInModal && !isInPreferenceCard;
  const shouldShowSEOContent = !isInModal;
  const shouldShowDetails = showDetailsEvenIfUnavailable || !isNotAvailableForSale;
  const shouldShowNearbyPlacesMap = !isInPreferenceCard;
  const shouldShowNearbyUnitsAndProjects = !isInPreferenceCard;
  const shouldShowDisclaimer = !isInPreferenceCard;
  const shouldShowCustomerCareWidget = !isNotAvailableForSale && !isInPreferenceCard;
  const showDisclaimer = supOne.groupId === "mb2DbRAM7wfjxjsfm";
  return <ErrorBoundary fallback={SUPErrorFallback}>
			<div className={`@container ${className} min-h-screen bg-white max-w-7xl mx-auto`}>
				{showDisclaimer && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
						<p className="text-yellow-800 text-lg font-semibold mb-4">Disclaimer:</p>
						<p className="text-sm text-gray-600">
							This is a bank-repossessed property and may carry pending liabilities, including but not limited to
							property tax, electricity, water, and maintenance charges. The transaction is facilitated through a third
							party, and an applicable service fee shall be borne by the buyer.
						</p>
					</div>}
				<div className="px-0 @4xl:px-4 relative">
					{isVisible && <UnitPageTracking unitId={id} unitName={supOne.projectName || ""} price={supOne.prices?.[0]} />}

					{shouldShowBreadcrumbs && <BreadcrumbsWrapper className="mt-6 mb-4 px-2 @4xl:px-0" city={cityName} slug={slug} supOne={supOne} />}

					{/* SEO Content - set in context for footer */}
					{shouldShowSEOContent && <SEOContentSetter content={<SEOContentForProjectOrSup projectName={supOne.projectName} locality={supOne.locality} propertyTypes={supOne.propertyTypes} availableUnitsCount={1} availableBedrooms={supOne.bedrooms} />} />}

					{/* Unit Title */}
					<SUPTitle supOne={supOne} className={`${isInPreferenceCard ? "mt-1 mb-2" : "mt-4 mb-6"} px-2 @4xl:px-0`} isLoading={isLoading} isNotAvailableForSale={isNotAvailableForSale} showDetailsEvenIfUnavailable={showDetailsEvenIfUnavailable} isInPreferenceCard={isInPreferenceCard} />
				</div>

				{shouldShowDetails && <div className="px-0 @4xl:px-4">
						<div className="w-full">
							{/* Mobile Layout */}
							<div className="@4xl:hidden mt-4">
								{/* Gallery moved below heading */}
								<div className="mb-3 h-[300px] -mx-0 @4xl:-mx-4">
									<GallerySmall mediaList={galleryImages} type={supOne.type} className="" projectId={supOne.projectId} coverImage={supOne.cover || supOne.appCoverImage} categories={categories} videoUrl={galleryVideo?.url} />
								</div>

								{/* Overview Section - Mobile Only */}
								<OverviewSection supOne={supOne} isLoading={isLoading} className="@4xl:hidden" />

								{/* Floor Plan */}
								{supOne.type === "unit" && floorplans.length > 0 && <SectionContent title={`Floor Plan${floorplans.length > 1 ? "s" : ""}`}>
										<div className="mb-3 h-[300px] @4xl:h-[800px] -mx-4">
											<GallerySmall mediaList={floorplans} videoUrl="" />
										</div>
									</SectionContent>}
							</div>

							{/* Desktop Layout */}
							<div className="hidden @4xl:block mt-4">
								{/* Main Content Grid */}
								<div className="grid grid-cols-1 @4xl:grid-cols-3 gap-8 mb-8">
									{/* Gallery Section */}
									<div className="@4xl:col-span-2 min-h-[500px]">
										<GalleryLarge mediaList={galleryImages} categories={categories} shouldPrioritizeCoverImage coverImage={supOne.cover || supOne.appCoverImage} projectId={supOne.projectId} type={supOne.type} videoUrl={galleryVideo?.url} videoCaptionsUrl={getVideoCaptionsUrl(galleryVideo?.url)} />
									</div>

									{/* Overview Section - Desktop Sidebar */}
									<OverviewSection supOne={supOne} isLoading={isLoading} className="@4xl:col-span-1" />
								</div>
								{/* Floor Plan */}
								{supOne.type === "unit" && floorplans.length > 0 && <SectionContent title={`Floor Plan${floorplans.length > 1 ? "s" : ""}`} className="@4xl:col-span-1">
										<div className="mb-3 h-[300px] @4xl:h-[800px] -mx-4">
											<GalleryLarge mediaList={floorplans} shouldPrioritizeCoverImage videoUrl="" />
										</div>
									</SectionContent>}
							</div>

							{supOne.type === "sp" && <SectionContent title="Configurations">
									<ProjectConfigurations configurations={configurations || defConfigs} />
								</SectionContent>}

							{/* Promotional Card Slot 0 - After Floor Plan/Configurations */}
							<PromotionalCardsSection pageType="sup" className="my-6" slot={0} totalSlots={6} />

							{/* Details Table */}
							<div className="grid grid-cols-1 @4xl:grid-cols-2 gap-8">
								<SectionContent title="Property Details">
									{supOne.identifier && <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
											<div className="text-xs text-gray-600">Property Id: {supOne.identifier}</div>
											<CopyButton text={supOne.identifier} iconSize="sm" variant="ghost" className="p-1" />
										</div>}
									<PropertyDetails supOne={supOne} isLoading={isLoading} />
								</SectionContent>
								<SectionContent title="Project Details">
									<ProjectDetails supOne={supOne} isLoading={isLoading} />
								</SectionContent>
							</div>

							{/* Promotional Card Slot 1 - After Property Details */}
							<PromotionalCardsSection pageType="sup" className="my-6" slot={1} totalSlots={6} />

							{/* Amenities */}
							{Boolean(supOne.amenities?.length) && <SectionContent title="Amenities">
									<AmenitiesSection amenities={supOne.amenities || []} isLoading={isLoading} />
								</SectionContent>}

							{/* Promotional Card Slot 2 - After Amenities */}
							<PromotionalCardsSection pageType="sup" className="my-6" slot={2} totalSlots={6} />

							{/* Additional Features */}
							{(supOne.additionalFeatures || []).length > 0 && <SectionContent title="Additional Features">
									<AdditionalFeatures additionalFeatures={supOne.additionalFeatures || []} />
								</SectionContent>}

							{/* Promotional Card Slot 3 - After Additional Features */}
							<PromotionalCardsSection pageType="sup" className="my-6" slot={3} totalSlots={6} />

							{/* Interiors and Flooring - Side by side on desktop */}
							{(Boolean(supOne.spaceSummary?.interiors?.length) || Boolean(supOne.spaceSummary?.flooring?.length) || Boolean(supOne.spaceSummary?.furnishing?.items?.length)) && <div className={`grid grid-cols-1 ${sectionsWithContent !== 1 ? "@4xl:grid-cols-2" : ""}  gap-8`}>
									{Boolean(supOne.spaceSummary?.interiors?.length) && <SectionContent title="Interiors">
											<Interiors supOne={supOne} />
										</SectionContent>}

									{Boolean(supOne.spaceSummary?.flooring?.length) && <SectionContent title="Flooring">
											<Flooring supOne={supOne} />
										</SectionContent>}

									{/* Furnishing */}
									{Boolean(supOne.spaceSummary?.furnishing?.items?.length) && sectionsWithContent !== 3 && <SectionContent title="Furnishing">
											<Furnishing supOne={supOne} />
										</SectionContent>}
								</div>}
							{Boolean(supOne.spaceSummary?.furnishing?.items?.length) && sectionsWithContent === 3 && <div className={`grid grid-cols-1 gap-8`}>
									<SectionContent title="Furnishing">
										<Furnishing supOne={supOne} />
									</SectionContent>
								</div>}

							{/* Promotional Card Slot 4 - After Interiors/Flooring */}
							<PromotionalCardsSection pageType="sup" className="my-6" slot={4} totalSlots={6} />

							{/* Sub Units */}
							{(supOne.subUnits || []).length > 0 && <SectionContent title={`Sub Units (${supOne.subUnits?.length})`}>
									{/* <SubUnitsSection subUnits={supOne.subUnits!} /> */}
									<SubUnitsSection subUnits={supOne.subUnits!} />
								</SectionContent>}

							{/* Promotional Card Slot 5 - After Sub Units */}
							<PromotionalCardsSection pageType="sup" className="my-6" slot={5} totalSlots={6} />

							{/* Nearby Places Map */}
							{shouldShowNearbyPlacesMap && <SectionContent title="Nearby Places">
									{coordinates && mapCoordinates && <LazyNearbyPlacesMap center={coordinates} coordinates={mapCoordinates} className="mb-6" />}
								</SectionContent>}

							{/* Disclaimer */}
						</div>
					</div>}
				{shouldShowNearbyUnitsAndProjects && <div className="px-0 @4xl:px-4">
						{supOne.type === "unit" && coordinates && <SectionContent title="Nearby Units">
								<LazySection rootMargin="150px" threshold={0.1} fallback={<NearbySupsSkeleton />} ssrShowChildren>
									<NearbySUPsClient coordinates={coordinates} loading={isLoading} initialData={_nearByUnits || []} distanceText={finalDistanceFromText} type="unit" excludeId={id} shouldPrioritizeCoverImage={false} isNotAvailableForSale={isNotAvailableForSale} unitId={id} />
								</LazySection>
							</SectionContent>}

						{/* Nearby Projects */}
						<SectionContent title="Nearby Projects">
							{coordinates && <LazySection rootMargin="150px" threshold={0.1} fallback={<NearbySupsSkeleton />} ssrShowChildren>
									<NearbySUPsClient coordinates={coordinates} loading={isLoading} initialData={nearByProjects} distanceText={finalDistanceFromText} type="sp" excludeId={id} shouldPrioritizeCoverImage={false} isNotAvailableForSale={isNotAvailableForSale} unitId={id} />
								</LazySection>}
						</SectionContent>
					</div>}
				{shouldShowDisclaimer && <div className="px-0 @4xl:px-4">
						<SectionContent title="Disclaimer" className="pt-1 pb-5 border-t border-gray-200">
							<div className="flex justify-between items-center text-xs">
								<span className="text-gray-600 text-xs text-justify">
									The contents of this website are for general information purposes only. While we verify the details
									with the owners/builders at regular intervals to ensure the accuracy of the data, we have not done any
									legal verification till date. Readers are requested to make an enquiry either independently or through
									us before relying upon the same. In no event will Settlin (Linking Spaces Technologies Pvt Ltd) offer
									any warranty on the information made available, or be liable for any loss or damage including without
									limitation, indirect or consequential loss or damage in connection with the use of information in this
									website. By using or accessing this website, you agree with this Disclaimer without any qualification
									or limitation. Design, specifications, floor plans, photographs, brochures and interactive plans etc.,
									are subject to change without prior notice. RERA ID: PRM/KA/RERA/1251/446/AG/180205/001849 (Linking
									Spaces Technologies Pvt Ltd)
								</span>
							</div>
						</SectionContent>
					</div>}

				{shouldShowCustomerCareWidget && <CustomerCareWidget {...supOne.type === "sp" ? {
        projectId: supOne.projectId
      } : supOne.type === "unit" ? {
        unitId: id
      } : {}} isInModal={isInModal} href={`/units/${slug}-${id}`} askingPrice={supOne.prices?.[0]} />}
			</div>
		</ErrorBoundary>;
}